import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@mui/material';
import CustomTextFieldAdornment from './shared/adornment';
import CustomTextFieldLabel from './shared/label';
import CustomTextFieldHelperText from './shared/helperText';

const CustomTextField = ({
  value: externalValue,
  setValue: setExternalValue,
  label = undefined,
  placeholder = undefined,
  disabled = false,
  fullWidth = false,
  helperText = undefined,
  valid = true,
  autoFocus = false,
  symbolsLimit = false,
  optional = false,
  className = '',
  labelClassName = '',
  type = 'text',
  prefix = null,
  postfix = null,
  multiline = false,
  rowsAmount = 4,
  id = undefined,
  name = undefined,
  onFocus = () => {},
  onBlur = () => {},
  min = undefined,
  max = undefined,
  step = undefined,
  readOnly = false,
  onClick = undefined,
  onInvalid = undefined,
}) => {
  const initiating = useRef(true);
  const [value, setValue] = useState(
    externalValue !== null && externalValue !== undefined ? externalValue : ''
  );
  const [modified, setModified] = useState(false);

  useEffect(() => {
    if (initiating.current) {
      initiating.current = false;
    } else {
      if (externalValue !== value) {
        setModified(true);
        setValue(externalValue);
      }
    }
  }, [externalValue]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (symbolsLimit && newValue.length > symbolsLimit) return;
    setModified(true);
    if (setExternalValue) setExternalValue(newValue, e);
    else setValue(newValue);
  };

  return (
    <TextField
      id={id}
      className={className}
      value={value}
      onInvalid={onInvalid}
      onChange={handleChange}
      error={!valid}
      InputLabelProps={{ className: labelClassName }}
      helperText={
        disabled ? null : (
          <CustomTextFieldHelperText
            text={helperText}
            value={value}
            disabled={disabled}
            valid={valid}
            limit={symbolsLimit}
          />
        )
      }
      autoFocus={autoFocus}
      disabled={disabled}
      label={<CustomTextFieldLabel text={label} optional={optional} />}
      placeholder={placeholder}
      fullWidth={fullWidth}
      type={type}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      InputProps={{
        startAdornment: prefix ? <CustomTextFieldAdornment content={prefix} /> : undefined,
        endAdornment: postfix ? <CustomTextFieldAdornment content={postfix} /> : undefined,
        className: modified ? 'custom-text-field-modified' : undefined,
        autoComplete: 'off',
        name,
        readOnly,
        inputProps: {
          min: type === 'number' ? min : undefined,
          max: type === 'number' ? max : undefined,
          id,
          step,
        },
      }}
      autoComplete="off"
      multiline={multiline}
      rows={multiline ? rowsAmount : undefined}
    />
  );
};

CustomTextField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setValue: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
  valid: PropTypes.bool,
  autoFocus: PropTypes.bool,
  symbolsLimit: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  optional: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.oneOf(['text', 'number', 'password', 'email', 'tel']),
  prefix: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.number]),
  postfix: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.number]),
  multiline: PropTypes.bool,
  rowsAmount: PropTypes.number,
  id: PropTypes.string,
  name: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  readOnly: PropTypes.bool,
  onClick: PropTypes.func,
};

export default CustomTextField;
